import { useMemo } from 'react';
import { Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import dayjs from 'dayjs';

import useStaff from '../../../../hooks/useStaff';
import api from '../../../../utils/api';


export type EvidenceLevel = 'off' | 'optional' | 'required';

export type EntryEvidence = {
  photos: EvidenceLevel;
  signature: EvidenceLevel;
};

const LEVELS: EvidenceLevel[] = ['off', 'optional', 'required'];
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];


/**
 * Configuración de responsivas de la comunidad (`settings.access_authorization`), con los
 * mismos defaults que la API (AccessAuthorization::entryEvidence / qrMode).
 */
export function useAccessAuthorizationConfig(communityId?: string) {
  const { staff } = useStaff();
  const community = staff?.find((c: any) => c.uuid === communityId);
  const config = community?.settings?.access_authorization ?? {};
  // Configuración → App de Vigilancia → "Autorización de acceso"; sin configurar se permite
  const guardCanCreate = community?.settings?.guard?.access_authorization !== false;
  // Módulo de autorizaciones activo (mismo criterio que el menú del panel web).
  const enabled = !!community?.settings?.access_authorization?.enabled;
  // Configuración → App de Vigilancia → "Reparto en caseta"; sin configurar se permite
  // (mismo default que esa pantalla).
  const boothDelivery = community?.settings?.guard?.booth_delivery !== false;

  return useMemo(() => {
    const raw = config.entry_evidence ?? {};
    const level = (v: any): EvidenceLevel => (LEVELS.includes(v) ? v : 'off');

    const evidence: EntryEvidence = {
      photos: level(raw.photos),
      signature: level(raw.signature),
    };

    return {
      evidence,
      serviceHours: config.service_hours ?? null,
      guardCanCreate,
      enabled,
      boothDelivery,
    };
  }, [config, guardCanCreate, enabled, boothDelivery]);
}


/** Informativo, igual que ServiceHours::isOutside en la API. */
export function isOutsideServiceHours(serviceHours: any, at = dayjs()) {
  if (!serviceHours || typeof serviceHours !== 'object') return false;

  const range = serviceHours[DAYS[at.day()]];
  if (!range?.from || !range?.to) return true;

  const time = at.format('HH:mm');
  return time < range.from || time > range.to;
}


/** Toma una foto solo con cámara (la evidencia tiene que ser del momento) y la sube. */
export async function takeAndUploadPhoto(communityId: string) {
  const perm = await ImagePicker.requestCameraPermissionsAsync();
  if (!perm.granted) {
    alert('Necesitamos permiso de cámara');
    return null;
  }

  const result = await ImagePicker.launchCameraAsync({ quality: 0.6 });
  if (result.canceled || !result.assets?.[0]) return null;

  const uri = result.assets[0].uri;
  const name = uri.split('/').pop() ?? 'image.jpg';
  const ext = /\.(\w+)$/.exec(name)?.[1] ?? 'jpg';

  const fd = new FormData();
  if (Platform.OS === 'web') {
    const blob = await (await fetch(uri)).blob();
    fd.append('image', blob, name);
  } else {
    // @ts-ignore RN file shape
    fd.append('image', { uri, name, type: `image/${ext}` });
  }

  const res = await api.post(`/communities/${communityId}/images`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return res.data as { uuid: string; url: string };
}
